// Better Auth + Quickback browser-cookie client.
//
// Mirrors apps/ios/Shared/Services/AuthStore.swift conceptually:
//   - native app: bearer/session token transport
//   - web admin: Better Auth's httpOnly cross-subdomain cookie
//   - JWT minting is retained only for platform-role checks and API access
//
// We cache only non-sensitive user display state in localStorage so the UI can
// render immediately. The real web session is the Better Auth cookie.
import {
  readStorage,
  removeStorage,
  removeStorageByPrefixExcept,
  writeStorage,
} from "./storage"
import { backendUrl } from "./backend-url"
import { apiFetch, clearD1Bookmark } from "./d1-bookmark"

const SESSION_USER_KEY = "attendvip_admin_user"

// Older organizer builds persisted privileged session and impersonation state
// under this namespace. Retain only the non-sensitive display-user cache and
// erase every retired artifact when the upgraded app first loads.
removeStorageByPrefixExcept("attendvip_admin_", [SESSION_USER_KEY])

export type SessionUser = {
  id: string
  email: string
  name: string | null
  emailVerified: boolean
  role?: string | null
}

type JwtCache = {
  token: string
  expiresAt: number
}

let jwtCache: JwtCache | null = null
let inFlightMint: Promise<string> | null = null
let inFlightSession: Promise<SessionUser | null> | null = null
let pendingOAuthRedirectUrl: string | null = null

function currentOAuthQuery(): string | undefined {
  if (typeof window === "undefined") return undefined
  const query = window.location.search.replace(/^\?/, "")
  return new URLSearchParams(query).has("sig") ? query : undefined
}

function captureOAuthRedirect(body: {
  url?: unknown
  redirect_uri?: unknown
}) {
  const candidate =
    typeof body.url === "string"
      ? body.url
      : typeof body.redirect_uri === "string"
        ? body.redirect_uri
        : null
  if (candidate) pendingOAuthRedirectUrl = candidate
}

export function takeOAuthRedirectUrl(): string | null {
  const url = pendingOAuthRedirectUrl
  pendingOAuthRedirectUrl = null
  return url
}

// In-memory result cache for ensureSession(), mirroring jwtCache. Without it
// every api() call hit /auth/v1/get-session over the wire (the inFlightSession
// dedup only collapses *concurrent* calls). The BA cookie is the real auth and
// its rolling lifetime is updateAge:1d, so re-confirming the session on a short
// TTL — instead of per request — is plenty fresh. A `force` re-check covers the
// 401 path and identity switches. `user: null` is cached too, so a signed-out
// browser doesn't hammer the endpoint either.
const SESSION_TTL_MS = 30_000
let sessionCache: { user: SessionUser | null; checkedAt: number } | null = null

export function getSessionUser(): SessionUser | null {
  const raw = readStorage(SESSION_USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as SessionUser
  } catch {
    return null
  }
}

function setCachedUser(user: SessionUser) {
  const wroteUser = writeStorage(SESSION_USER_KEY, JSON.stringify(user))
  if (!wroteUser) {
    removeStorage(SESSION_USER_KEY)
    throw new Error("This browser blocked local storage, so the admin user cache could not be saved")
  }
  jwtCache = null
  // Keep the session cache in step with a freshly-known user (e.g. right after
  // sign-in) so the next api() call doesn't re-hit get-session to learn what we
  // just established.
  sessionCache = { user, checkedAt: Date.now() }
}

export function clearSession() {
  removeStorage(SESSION_USER_KEY)
  jwtCache = null
  sessionCache = null
}

export async function ensureSession(
  opts: { force?: boolean } = {},
): Promise<SessionUser | null> {
  // Serve from the in-memory cache while it's fresh. `force` skips it — used by
  // the 401 retry path, where the whole point is to re-confirm the session.
  if (
    !opts.force &&
    sessionCache &&
    Date.now() - sessionCache.checkedAt < SESSION_TTL_MS
  ) {
    return sessionCache.user
  }
  // `force` must also bypass the in-flight dedup: piggybacking on a get-session
  // that started BEFORE this point (e.g. a boot check that ran pre-cookie) would
  // return its stale `null` — exactly the "signed in but no session" symptom
  // right after sign-in. A forced call always issues its own fresh request.
  if (!opts.force && inFlightSession) return inFlightSession
  inFlightSession = (async () => {
    try {
      const res = await apiFetch(backendUrl("/auth/v1/get-session"), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      })
      if (!res.ok) {
        clearSession()
        sessionCache = { user: null, checkedAt: Date.now() }
        return null
      }
      const body = (await res.json().catch(() => null)) as { user?: SessionUser | null } | null
      if (!body?.user) {
        clearSession()
        sessionCache = { user: null, checkedAt: Date.now() }
        return null
      }
      setCachedUser(body.user) // also refreshes sessionCache
      return body.user
    } finally {
      inFlightSession = null
    }
  })()
  return inFlightSession
}

export async function sendEmailOtp(email: string): Promise<void> {
  const oauth_query = currentOAuthQuery()
  const res = await apiFetch(backendUrl("/auth/v1/email-otp/send-verification-otp"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, type: "sign-in", oauth_query }),
  })
  if (!res.ok) throw new Error(await readError(res))
}

/// Step 1 of the hidden set-password flow at /set-password. Sends a
/// 6-digit OTP keyed to "forget-password". BA returns success even
/// for unknown emails (intentional — avoids account-existence leak),
/// so a 200 doesn't actually mean the email exists.
export async function requestPasswordResetOtp(email: string): Promise<void> {
  const res = await apiFetch(backendUrl("/auth/v1/email-otp/request-password-reset"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  })
  if (!res.ok) throw new Error(await readError(res))
}

/// Step 2 of /set-password. Submits {email, otp, password} to BA's
/// /email-otp/reset-password endpoint. For invitee accounts that have
/// never had a credentials row (the normal AttendVIP path — see
/// `sendInvite.ts`), BA inserts the `credential` account row instead
/// of updating it, so this doubles as a first-time set.
export async function confirmPasswordReset(
  email: string,
  otp: string,
  password: string,
): Promise<void> {
  const res = await apiFetch(backendUrl("/auth/v1/email-otp/reset-password"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, otp, password }),
  })
  if (!res.ok) throw new Error(await readError(res))
}

export async function signInWithOtp(
  email: string,
  otp: string
): Promise<SessionUser> {
  const oauth_query = currentOAuthQuery()
  const res = await apiFetch(backendUrl("/auth/v1/sign-in/email-otp"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, otp, oauth_query }),
  })
  if (!res.ok) throw new Error(await readError(res))

  const body = (await res.json()) as {
    user?: SessionUser
    url?: string
    redirect_uri?: string
  }
  captureOAuthRedirect(body)
  // The web session is the httpOnly BA cookie. Do not trust `body.user` alone:
  // if the browser rejected/dropped Set-Cookie, the app would otherwise render
  // as signed in until the first API call 401s.
  const user = await ensureSession({ force: true })
  if (!user) throw new Error("Sign-in succeeded but no session was returned")
  setCachedUser(user)
  return user
}

/// Updates the signed-in user's display name via Better Auth's
/// update-user endpoint, then patches the locally-cached SessionUser so
/// the header/avatar reflect the change without a re-fetch.
/// Secondary password sign-in (the primary path is email OTP). Hits Better
/// Auth's email+password endpoint and establishes the cookie session the same
/// way as signInWithOtp. Not every account has a password (OTP-only invitees),
/// so a 401 here is expected for those — surface it as a friendly error.
export async function signInWithPassword(
  email: string,
  password: string
): Promise<SessionUser> {
  const oauth_query = currentOAuthQuery()
  const res = await apiFetch(backendUrl("/auth/v1/sign-in/email"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password, oauth_query }),
  })
  if (!res.ok) throw new Error(await readError(res))

  const body = (await res.json()) as {
    user?: SessionUser
    url?: string
    redirect_uri?: string
  }
  captureOAuthRedirect(body)
  // The web session is the httpOnly BA cookie. Do not trust `body.user` alone:
  // if the browser rejected/dropped Set-Cookie, the app would otherwise render
  // as signed in until the first API call 401s.
  const user = await ensureSession({ force: true })
  if (!user) throw new Error("Sign-in succeeded but no session was returned")
  setCachedUser(user)
  return user
}

export async function updateDisplayName(name: string): Promise<void> {
  const res = await apiFetch(backendUrl("/auth/v1/update-user"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw new Error(await readError(res))
  const user = getSessionUser()
  if (user) writeStorage(SESSION_USER_KEY, JSON.stringify({ ...user, name }))
}

export async function signOut(): Promise<void> {
  try {
    // The HttpOnly Better Auth cookies are cleared server-side: the backend
    // sign-out deletes its host cookie, and the worker proxy additionally
    // expires the shared-parent-domain (.attend.vip) cookie so a stale prod
    // session can't shadow the next sign-in (see apps/organizer-web/src/server.ts).
    await apiFetch(backendUrl("/auth/v1/sign-out"), {
      method: "POST",
      credentials: "include",
    })
  } catch {
    // Best-effort — local clear still proceeds even if the network call fails.
  }
  clearReadableCookies()
  clearSession()
  // Drop the D1 read-replication bookmark so the next user in this tab starts
  // a fresh session position (after the sign-out response was recorded).
  clearD1Bookmark()
}

// Belt-and-suspenders client sweep: expire every NON-HttpOnly cookie readable
// here, across this host and the shared parent domain. The session cookie is
// HttpOnly (invisible to JS — only the server can clear it), so this just mops
// up any incidental readable cookies; the real session clear is server-side.
function clearReadableCookies() {
  if (typeof document === "undefined") return
  const host = typeof window !== "undefined" ? window.location.hostname : ""
  // e.g. aaa-2.dev.attend.vip → ["aaa-2.dev.attend.vip", ".dev.attend.vip",
  // ".attend.vip"] — cover host-only plus each parent-domain scope.
  const domains = [""]
  const parts = host.split(".")
  for (let i = 0; i < parts.length - 1; i++) domains.push("." + parts.slice(i).join("."))
  for (const entry of document.cookie.split(";")) {
    const name = entry.split("=")[0]?.trim()
    if (!name) continue
    for (const domain of domains) {
      document.cookie = `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT${domain ? `; Domain=${domain}` : ""}`
    }
  }
}

/// Fetches a short-lived QB JWT. Cached in-memory until 30s before exp.
/// Concurrent callers share a single in-flight mint so we don't burn
/// roundtrips on the cold-start fan-out from a freshly-loaded route.
export async function currentJwt(): Promise<string> {
  const now = Math.floor(Date.now() / 1000)
  if (jwtCache && jwtCache.expiresAt - 30 > now) return jwtCache.token
  if (inFlightMint) return inFlightMint

  inFlightMint = (async () => {
    try {
      const res = await apiFetch(backendUrl("/auth/v1/token"), {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) throw new Error(await readError(res))
      const body = (await res.json()) as { token: string }
      // /auth/v1/token only returns `{ token }`. Decode the JWT to read
      // `exp` ourselves so the cache can refresh ahead of expiry.
      const expiresAt = decodeJwtExp(body.token) ?? now + 60
      jwtCache = { token: body.token, expiresAt }
      return body.token
    } finally {
      inFlightMint = null
    }
  })()
  return inFlightMint
}

export function invalidateJwt() {
  jwtCache = null
}

function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split(".")[1]
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    const obj = JSON.parse(json) as { exp?: number }
    return typeof obj.exp === "number" ? obj.exp : null
  } catch {
    return null
  }
}

/// Returns the platform-level user role pulled from the `userRole`
/// claim on the cached QB JWT. Mints a JWT if none is cached. The
/// value comes from Better Auth's `user.role` column and is distinct
/// from per-org `member.role`.
export async function currentUserRole(): Promise<string | null> {
  try {
    const token = await currentJwt()
    const payload = token.split(".")[1]
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    const obj = JSON.parse(json) as { userRole?: string }
    return obj.userRole ?? null
  } catch {
    return null
  }
}

export async function isControlPlaneUser(): Promise<boolean> {
  const role = await currentUserRole()
  return role === "appmanager" || role === "sysadmin"
}

export async function isSysadmin(): Promise<boolean> {
  return (await currentUserRole()) === "sysadmin"
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string; error?: string }
    return body.message ?? body.error ?? `${res.status} ${res.statusText}`
  } catch {
    return `${res.status} ${res.statusText}`
  }
}
